import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

const OFFERS = {
  bottle: {
    tag: 'On-the-go pick',
    title: 'Fresh 8 ppm hydrogen — anywhere.',
    body: 'Get the Echo Flask + a 2-month hydrogen prebiotic supply FREE ($120 value).',
    cta: 'Claim the Flask Bundle →',
    url: 'https://echowater.com/products/flask-hydrogen-prebiotic-bundle?ref=ninja',
  },
  machine: {
    tag: 'For your home',
    title: 'Unlimited hydrogen water at home.',
    body: 'The new Echo One pairs reverse-osmosis purity with on-demand hydrogen for the whole household.',
    cta: 'Explore Home Machines →',
    url: 'https://echowater.com/collections/all/products/echo-one-machine?ref=ninja',
  },
  budget: {
    tag: 'Easiest way to try it',
    title: 'Try hydrogen water for under $40.',
    body: 'Drop an Echo prebiotic stick in any water — hydrogen boost + gut support, no device needed.',
    cta: 'Try Prebiotic Sticks →',
    url: 'https://echowater.com/collections/hydrogen-water/products/hydrogen-prebiotic-stick-pack-30-pack?ref=ninja',
  },
  filter: {
    tag: 'Clean water first',
    title: 'Purify before you hydrogenate.',
    body: 'Echo RO delivers tankless reverse-osmosis water that removes up to 99% of contaminants.',
    cta: 'Shop Echo RO →',
    url: 'https://echowater.com/collections/all/products/echo-ro-reverse-osmosis?ref=ninja',
  },
  default: {
    tag: 'Recommended',
    title: 'Fresh hydrogen water, anywhere.',
    body: 'Start with the Echo Flask + a 2-month hydrogen prebiotic supply free.',
    cta: 'Shop the Echo Flask →',
    url: 'https://echowater.com/pages/echo-pro-flask?ref=ninja',
  },
};

const INTENT_KEYWORDS = {
  machine: ['machine', 'countertop', 'under-sink', 'under sink', 'home', 'family', 'whole home', 'whole-home', 'install', 'ultimate', 'echo one', 'echo h2', 'server', 'flow', 'household'],
  filter: ['reverse osmosis', 'reverse-osmosis', ' ro ', 'ro water', 'filter', 'filtration', 'contaminant', 'fluoride', 'lead', 'pfas', 'chlorine'],
  budget: ['tablet', 'tablets', 'stick', 'sticks', 'prebiotic', 'cheap', 'budget', 'affordable', 'walgreens', 'cvs', 'try'],
  bottle: ['bottle', 'bottles', 'flask', 'tumbler', 'portable', 'forty', 'near me', 'nearby', 'on the go', 'on-the-go', 'travel', 'gym', 'in store'],
};

const textIntent = (t) => {
  const text = (t || '').toLowerCase();
  for (const key of Object.keys(INTENT_KEYWORDS)) {
    if (INTENT_KEYWORDS[key].some((w) => text.indexOf(w.trim()) > -1)) return key;
  }
  return null;
};

/**
 * Intent-aware Echo Water affiliate popup. Slides in bottom-right on the
 * hydrogen water article (popup_config.type === 'echo_offer'). Shows once per
 * session after 25s / 40% scroll / exit-intent, picking the offer that best
 * matches the visitor's search query or the sections they've been reading.
 */
export const EchoOfferPopup = () => {
  const [offer, setOffer] = useState(null);
  const [visible, setVisible] = useState(false);
  const shownRef = useRef(false);
  const tallyRef = useRef({ bottle: 0, machine: 0, budget: 0, filter: 0 });

  useEffect(() => {
    try { if (sessionStorage.getItem('njEchoPopDone')) return undefined; } catch (e) { /* private mode */ }

    const paramIntent = () => {
      try {
        const q = `${new URLSearchParams(window.location.search).toString().toLowerCase()} ${(document.referrer || '').toLowerCase()}`;
        return textIntent(q);
      } catch (e) { return null; }
    };
    const scrollIntent = () => {
      let best = null; let max = 0;
      Object.entries(tallyRef.current).forEach(([k, v]) => { if (v > max) { max = v; best = k; } });
      return max > 0 ? best : null;
    };
    const show = () => {
      if (shownRef.current) return;
      shownRef.current = true;
      const key = paramIntent() || scrollIntent() || 'default';
      setOffer(OFFERS[key] || OFFERS.default);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    };

    let io;
    const ioTimer = setTimeout(() => {
      if ('IntersectionObserver' in window) {
        const heads = document.querySelectorAll('.hw-wrap h2, .hw-wrap h3, .product-card h3, .product-card h4');
        io = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              const i = textIntent(en.target.textContent);
              if (i) tallyRef.current[i] += 1;
            }
          });
        }, { threshold: 0.6 });
        heads.forEach((h) => io.observe(h));
      }
    }, 1200);

    const timer = setTimeout(show, 25000);
    const onScroll = () => {
      const sc = window.scrollY / (document.body.scrollHeight - window.innerHeight || 1);
      if (sc >= 0.4) show();
    };
    const onMouseOut = (e) => { if (e.clientY <= 0 && !e.relatedTarget) show(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mouseout', onMouseOut);

    return () => {
      clearTimeout(timer);
      clearTimeout(ioTimer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onMouseOut);
      if (io) io.disconnect();
    };
  }, []);

  const close = () => {
    setVisible(false);
    setTimeout(() => setOffer(null), 400);
    try { sessionStorage.setItem('njEchoPopDone', '1'); } catch (e) { /* private mode */ }
  };

  if (!offer) return null;

  return (
    <div
      role="dialog"
      aria-label="Echo hydrogen water offer"
      data-testid="echo-offer-popup"
      className="fixed right-4 bottom-24 sm:right-5 z-40 max-w-[340px] w-[calc(100%-32px)] bg-[#0b1220] text-[#eaf2ff] border border-[#1e2a44] rounded-2xl shadow-[0_18px_50px_rgba(0,0,0,0.45)] p-5"
      style={{
        transform: visible ? 'translateY(0)' : 'translateY(140%)',
        opacity: visible ? 1 : 0,
        transition: 'transform .5s cubic-bezier(.16,1,.3,1), opacity .4s',
      }}
    >
      <button
        onClick={close}
        aria-label="Close"
        data-testid="echo-offer-close"
        className="absolute top-2.5 right-3 text-[#7d8db0] hover:text-white transition-colors p-1"
      >
        <X className="w-4 h-4" />
      </button>
      <span className="inline-block text-[10.5px] tracking-[0.08em] uppercase font-extrabold text-[#06282d] bg-[#38e2d0] px-2.5 py-1 rounded-full mb-2.5" data-testid="echo-offer-tag">
        {offer.tag}
      </span>
      <h4 className="m-0 mb-1.5 text-[1.12rem] leading-tight text-white font-semibold" data-testid="echo-offer-title">{offer.title}</h4>
      <p className="m-0 mb-3.5 text-sm leading-relaxed text-[#b8c6e0]">{offer.body}</p>
      <a
        href={offer.url}
        target="_blank"
        rel="noopener sponsored"
        data-testid="echo-offer-cta"
        className="block text-center bg-[#38e2d0] !text-[#06282d] font-extrabold no-underline py-3 px-4 rounded-xl hover:bg-[#59ffec] hover:-translate-y-0.5 transition-all"
      >
        {offer.cta}
      </a>
      <span className="block mt-2.5 text-center text-[11px] text-[#6f7fa3]">
        Trusted by athletes &amp; biohackers ·{' '}
        <a href="https://echowater.com/?ref=ninja" target="_blank" rel="noopener sponsored" className="text-[#9fb0d6] underline">
          See all products
        </a>
      </span>
    </div>
  );
};
